import { Redirect, useLocalSearchParams } from 'expo-router';
import { useAtomValue } from 'jotai';

import UserProfile from '@/components/userProfile';
import { userIdAtom } from '@/services/globalState';

/**
 * Canonicalises the self-referring profile URL before the screen renders.
 *
 * The nav bar and the account tab both link here without knowing the signed-in
 * id, which is convenient for them and awkward for everything underneath: the
 * screen, the data hook and every component below would each have to know the
 * route param might be a word rather than a number. Resolving it once here means
 * they never do, and one profile has one address, so it also cannot end up
 * cached twice under two spellings of the same user.
 *
 * Every other parameter is carried across, which is not incidental: a
 * notification deep-links to a section of the profile, and a redirect that kept
 * only the id would land the reader at the top of the page having silently
 * dropped the part that said where to go.
 *
 * Rendering nothing while the id is unknown is deliberate. This route is inside
 * the authenticated group, which redirects an unauthenticated viewer out on its
 * own, so the id arrives moments later. Redirecting before it does would send
 * the viewer to a profile for nobody.
 */
export default function ProfileRoute() {
  const { id, ...rest } = useLocalSearchParams<{ id: string }>();

  const currentUserId = useAtomValue(userIdAtom);

  if (id === 'me') {
    if (!currentUserId) {
      return null;
    }

    return (
      <Redirect
        href={{
          pathname: '/profile/[id]',
          params: { ...rest, id: String(currentUserId) },
        }}
      />
    );
  }

  return <UserProfile />;
}
